import { assign, createMachine, pure, sendParent, sendTo, spawn } from 'xstate';
import getDCHMessageType from '../../helpers/getDCHMessageType';
import { accountActivityAtom, invalidateAccountActivity } from '../../api/accountActivity';
import { PAGE_FUNCTION, SUB_FUNCTION } from '../../const/analytics';
import { ONE_TIME_PAYMENT_MACHINE_STATES_NAMES } from '../../const/machines';
import { PINLESS_DEBIT_CARD } from '../../const/paymentMedium';
import { getEstDateString } from '../../helpers';
import isDCHEligible from '../../helpers/isDCHEligible';
import { isSecuredInstallmentLoanProduct } from '../../helpers/productTypeHelpers';
import makeAPaymentStore from '../../store';
import { getHasMatchingPayment } from '../../ui/molecules/PaymentModalStates/helpers';
import { formMachine, getValues } from '../../xform';
import { getPaymentFlowFormValues } from '../helpers';
import paymentMachine from '../payment/paymentMachine';
import { getIsValidAutopayPaymentOption, savePaymentResult, saveShouldShowOneClickAutopay, submitFailureAnalyticsHandler } from './helpers';
import paymentAmountConfig from './paymentAmountConfig';
import paymentDateConfig from './paymentDateConfig';
import paymentMethodConfig from './paymentMethodConfig';
import paymentOptionConfig from './paymentOptionConfig';
export default createMachine({
    predictableActionArguments: true,
    id: 'oneTimeMachine',
    initial: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.INITIAL,
    schema: {
        events: {}
    },
    context: {
        apiMachineRef: undefined,
        errorType: undefined,
        featureFlags: undefined,
        form: undefined,
        shouldEnrollInOneClickAutopay: false,
        shouldShowOneClickAutopay: false,
        triggerAnalyticsEvent: () => undefined
    },
    states: {
        edit: {
            entry: [
                sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
                assign({
                    form({ apiMachineRef, form }) {
                        if (form) {
                            return form;
                        }
                        return spawn(formMachine.withContext({
                            fieldConfigs: [
                                // DO NOT CHANGE: the order of these configs must mirror the position of the inputs in the UI for the error focus to work
                                paymentDateConfig(apiMachineRef),
                                paymentOptionConfig(apiMachineRef),
                                paymentAmountConfig(apiMachineRef),
                                paymentMethodConfig(apiMachineRef)
                            ]
                        }), {
                            name: 'oneTimePaymentForm'
                        });
                    }
                })
            ],
            on: {
                SET_NEW_PAYMENT_METHOD: {
                    actions: [
                        sendTo(({ apiMachineRef }) => apiMachineRef, {
                            type: 'FETCH_PAYMENT_METHODS'
                        }),
                        'sendToPaymentMethodField'
                    ]
                },
                SET_CURRENT_DATE: {
                    actions: ['updateDatePickerField']
                },
                NEXT: {
                    target: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.REVIEW,
                    cond: 'hasNoMatchingPendingPayment',
                    actions: [
                        'saveShouldShowOneClickAutopay',
                        'updateOneClickAutopayEnrollment'
                    ]
                }
            }
        },
        review: {
            entry: sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
            on: {
                EDIT: {
                    target: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.EDIT,
                    actions: [
                        'updateOneClickAutopayEnrollment',
                        {
                            type: 'triggerAnalyticsEvent',
                            eventOptions: (context) => {
                                const { form, apiMachineRef, featureFlags } = context;
                                const oneTimePaymentForm = getValues(form.getSnapshot().context.actors);
                                const { accountSummaryQueryResult: { data: accountSummary } } = apiMachineRef.getSnapshot().context;
                                const messageTypeDCH = getDCHMessageType(featureFlags.isDigitalCollectionsEnabled, accountSummary);
                                return Object.assign({ message: oneTimePaymentForm.paymentOption, pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS, pageName: 'edit', pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT }, (messageTypeDCH && { messageType: messageTypeDCH }));
                            }
                        }
                    ]
                },
                SUBMIT: {
                    target: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.SUBMITTING
                },
                ONE_CLICK_AUTOPAY_ENROLLMENT: {
                    actions: 'updateOneClickAutopayEnrollment'
                }
            }
        },
        submitting: {
            entry: sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
            invoke: {
                id: 'payment',
                src: paymentMachine,
                data: ({ apiMachineRef, featureFlags, form, shouldEnrollInOneClickAutopay }) => {
                    const context = {
                        apiMachineRef,
                        featureFlags,
                        oneTimePaymentFormMachineRef: form,
                        shouldEnrollInOneClickAutopay
                    };
                    return context;
                },
                onDone: {
                    target: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.SUCCESS,
                    actions: ['savePaymentResult']
                },
                onError: {
                    target: ONE_TIME_PAYMENT_MACHINE_STATES_NAMES.ERROR,
                    actions: [
                        'savePaymentResult',
                        {
                            type: 'triggerAnalyticsEvent',
                            eventOptions: submitFailureAnalyticsHandler
                        },
                        sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' })
                    ]
                }
            }
        },
        success: {
            entry: [
                sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
                sendParent({ type: 'INCREMENT_CONVERSION' }),
                /*
                 * Secured Installment uses PaymentEligibility instead of AccountActivity to prevent further Payment
                 * so we need to force fetch eligibility
                 */
                sendTo(({ apiMachineRef }) => apiMachineRef, {
                    type: 'FETCH_PAYMENT_ELIGIBILITY',
                    force: true
                }),
                /*
                 * We need to force fetch account activities so that this payment transaction
                 * would show up in MMA. We also need it to be accurately reflected in `make-a-payment`
                 * because it's used to prevent a user from making a payment with the same
                 * payment method/date combo
                 */
                'shouldInvokeAccountActivity'
            ]
        },
        error: {},
        cancelled: {
            type: 'final',
            entry: sendParent({ type: 'CANCEL' })
        }
    },
    on: {
        CANCEL: {
            target: 'cancelled',
            actions: {
                type: 'triggerAnalyticsEvent',
                eventOptions: (context) => {
                    const { isDigitalCollectionsEnabled } = context.featureFlags;
                    const { accountSummaryQueryResult: { data: accountSummary } } = context.apiMachineRef.getSnapshot().context;
                    const messageTypeDCH = getDCHMessageType(isDigitalCollectionsEnabled, accountSummary);
                    return Object.assign({ pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS, pageName: 'cancel', pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT }, (messageTypeDCH && { messageType: messageTypeDCH }));
                }
            }
        }
    }
}, {
    actions: {
        triggerAnalyticsEvent: (context, event, meta) => {
            const { triggerAnalyticsEvent } = context;
            triggerAnalyticsEvent(meta.action.eventOptions(context));
        },
        shouldInvokeAccountActivity: pure((context) => {
            const { apiConfig, queryClient } = context.apiMachineRef.getSnapshot().context;
            invalidateAccountActivity(apiConfig, queryClient);
            return undefined;
        }),
        saveShouldShowOneClickAutopay,
        savePaymentResult,
        updateOneClickAutopayEnrollment: assign({
            shouldEnrollInOneClickAutopay: (context, { enrollment }) => {
                const { accountSummaryQueryResult: { data: accountSummary } } = context.apiMachineRef.getSnapshot().context;
                const { form } = context;
                const { paymentAmount, paymentOption } = getValues(form.getSnapshot().context.actors);
                const [, paymentMethod] = getPaymentFlowFormValues(context);
                return (enrollment &&
                    getIsValidAutopayPaymentOption(accountSummary, paymentOption, paymentAmount, paymentMethod));
            }
        }),
        sendToPaymentMethodField: sendTo(({ form }) => {
            return form.getSnapshot().context.actors.paymentMethod;
        }, (_, { paymentMethodId }) => {
            return {
                type: 'CHANGE',
                value: paymentMethodId
            };
        }),
        updateDatePickerField: pure((context) => {
            const { data: { product, billing } } = context.apiMachineRef.getSnapshot().context.accountSummaryQueryResult;
            const isDCH = isDCHEligible(billing, product.type, context.featureFlags);
            if (!isDCH)
                return undefined;
            const [, paymentMethod] = getPaymentFlowFormValues(context);
            const isDebit = paymentMethod.paymentMedium === PINLESS_DEBIT_CARD;
            if (!isDebit)
                return undefined;
            return sendTo(({ form }) => {
                return form.getSnapshot().context.actors.paymentDate;
            }, () => {
                return {
                    type: 'CHANGE',
                    value: getEstDateString(new Date())
                };
            });
        })
    },
    guards: {
        hasNoMatchingPendingPayment: ({ apiMachineRef, form }) => {
            const formMachineSnapshot = form.getSnapshot();
            const { product } = apiMachineRef.getSnapshot().context.accountSummaryQueryResult.data;
            if (isSecuredInstallmentLoanProduct(product.type))
                return true;
            const { activity } = makeAPaymentStore.get(accountActivityAtom).data;
            const { paymentDate, paymentMethod } = getValues(formMachineSnapshot.context.actors);
            const hasMatchingPayment = getHasMatchingPayment(paymentDate, paymentMethod, activity);
            return !hasMatchingPayment;
        }
    }
});
